import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Search, MapPin, ShoppingBag, MessageCircle } from 'lucide-react';
import { apiClient } from '../lib/api.js';
import { Card } from '../components/ui/Card.js';
import { Badge } from '../components/ui/Badge.js';
import { Input } from '../components/ui/Input.js';
import { Button } from '../components/ui/Button.js';
import {
  cn,
  formatRelative,
  stageLabel,
  stageColor,
  temperatureLabel,
  temperatureColor,
  formatPercent,
} from '../lib/utils.js';

const temperatures = [
  { value: '', label: 'Todas' },
  { value: 'quente', label: '🔥 Quente' },
  { value: 'morno', label: '🌡️ Morno' },
  { value: 'frio', label: '❄️ Frio' },
  { value: 'gelado', label: '🧊 Gelado' },
];

const stages = [
  { value: '', label: 'Todos' },
  { value: 'visitor', label: 'Visitante' },
  { value: 'engaged', label: 'Engajado' },
  { value: 'identified', label: 'Identificado' },
  { value: 'checkout_started', label: 'Checkout' },
  { value: 'customer_front', label: 'Front' },
  { value: 'customer_pro', label: 'Pro' },
  { value: 'active_customer', label: 'Ativo' },
  { value: 'at_risk', label: 'Em risco' },
  { value: 'won', label: 'Ganho' },
];

export function Contacts(): JSX.Element {
  const [temperature, setTemperature] = useState('');
  const [stage, setStage] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // debounce simples
  const handleSearch = (v: string) => {
    setSearch(v);
    const t = setTimeout(() => setDebouncedSearch(v), 300);
    return () => clearTimeout(t);
  };

  const { data, isLoading } = useQuery({
    queryKey: ['contacts', temperature, stage, debouncedSearch],
    queryFn: () =>
      apiClient.contactsList({
        temperature: temperature || undefined,
        stage: stage || undefined,
        search: debouncedSearch || undefined,
        limit: 100,
      }),
  });

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <header className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Contatos</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {data ? `${data.total} contatos no total — mostrando ${data.contacts.length}` : 'Carregando...'}
        </p>
      </header>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome, email, cidade..."
            className="pl-8"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>

        <select
          value={temperature}
          onChange={(e) => setTemperature(e.target.value)}
          className="h-9 rounded-md border border-border bg-background px-3 text-sm"
        >
          {temperatures.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>

        <select
          value={stage}
          onChange={(e) => setStage(e.target.value)}
          className="h-9 rounded-md border border-border bg-background px-3 text-sm"
        >
          {stages.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>

        {(temperature || stage || debouncedSearch) && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setTemperature('');
              setStage('');
              setSearch('');
              setDebouncedSearch('');
            }}
          >
            Limpar
          </Button>
        )}
      </div>

      {/* Table */}
      <Card>
        <table className="w-full text-sm">
          <thead className="text-xs text-muted-foreground bg-muted/30 border-b border-border">
            <tr>
              <th className="px-4 py-2 text-left font-medium">Contato</th>
              <th className="px-4 py-2 text-left font-medium">Stage</th>
              <th className="px-4 py-2 text-left font-medium">Temperatura</th>
              <th className="px-4 py-2 text-left font-medium">Score</th>
              <th className="px-4 py-2 text-left font-medium">Localização</th>
              <th className="px-4 py-2 text-left font-medium">UTM</th>
              <th className="px-4 py-2 text-center font-medium">Pedidos</th>
              <th className="px-4 py-2 text-center font-medium">Conv.</th>
              <th className="px-4 py-2 text-right font-medium">Último visto</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={9} className="text-center py-8 text-muted-foreground">Carregando...</td></tr>
            ) : data?.contacts.length === 0 ? (
              <tr><td colSpan={9} className="text-center py-8 text-muted-foreground">Nenhum contato encontrado.</td></tr>
            ) : (
              data?.contacts.map((c) => (
                <tr key={c.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition">
                  <td className="px-4 py-3">
                    <Link to={`/contatos/${c.id}`} className="block">
                      <div className="font-medium">{c.displayName ?? 'Sem nome'}</div>
                      <div className="text-xs text-muted-foreground">{c.emailDisplay}</div>
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={cn(stageColor[c.lifecycleStage] ?? '')}>
                      {stageLabel[c.lifecycleStage] ?? c.lifecycleStage}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={cn(temperatureColor[c.temperature] ?? '')}>
                      {temperatureLabel[c.temperature] ?? c.temperature}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">
                    {c.scorePurchase ? formatPercent(c.scorePurchase) : '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {c.geoCity}{c.geoState ? `, ${c.geoState}` : ''}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {c.utm?.utm_source ? (
                      <div>
                        <div>{c.utm.utm_source}</div>
                        {c.utm.utm_campaign && <div className="text-[10px]">{c.utm.utm_campaign}</div>}
                      </div>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {Number(c.totalOrders) > 0 ? (
                      <span className="inline-flex items-center gap-1 text-emerald-400">
                        <ShoppingBag className="h-3 w-3" />
                        {c.totalOrders}
                      </span>
                    ) : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {Number(c.openConversations) > 0 ? (
                      <span className="inline-flex items-center gap-1 text-blue-400">
                        <MessageCircle className="h-3 w-3" />
                        {c.openConversations}
                      </span>
                    ) : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right text-xs text-muted-foreground">
                    {formatRelative(c.lastSeenAt)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
